import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import ApplicableAgreementsSelect from "view/calculationmethods/components/select/ApplicableAgreementsSelect";
import { Fragment, ReactNode } from "react";
import FormField from "components/cck/FormField";
import NumberInput from "components/cck/NumberInput";
import { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";
import { usePermission } from "components/utils/Permission";
import AgreementTypeSelect from "view/agreements/components/selects/AgreementTypeSelect";
import { AgreementTypeEnum, CalculationMethodType, CharterSubtype, CharterType } from "__generated__/enums";
import { useStoreState } from "ezStore";
import { hasNegativePercentage } from "view/calculationmethods/utils/NegativeCommissionUtils";

type FormValues = {
	charterType: CharterType;
	charterSubtype: CharterSubtype;
	agreements: {
		id?: number;
		uuid?: string;
		type: AgreementTypeEnum | null;
		calculationMethod: {
			type: CalculationMethodType | null;
			percentage?: number;
			applicableAgreementTypes?: AgreementTypeEnum[];
			applicableAgreements?: {
				id?: number;
				uuid?: string;
				type: AgreementTypeEnum | null;
				calculationMethod: {
					type: CalculationMethodType | null;
				} | null;
			}[];
		}
	}[];
}


type FormField = {
	id: string;
	render: () => ReactNode;
}

const PercentageOnAgreementsCalculationMethodForm = ({ agreementTypes, customAgreementTypes, hiddenFields, index }: CalculationMethodFormByTypeProps) => {
	const { isTankmatch } = usePermission();
	const session = useStoreState(store => store.session);

	const { control, formState: { errors }, setValue } = useFormContext<FormValues>();

	const agreementType = useWatch({ control, name: `agreements.${index}.type` });
	const standardNegative = hasNegativePercentage(agreementType, session);

	const charterType = useWatch({ control, name: "charterType" });
	const charterSubtype = useWatch({ control, name: "charterSubtype" });
	const agreements = useWatch({ control, name: "agreements" }) ?? [];

	const PercentageOnAgreementsCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.percentage",
			render: () => (
				<>
					<Grid size={4}>
						<FormField errors={errors} name={`agreements.${index}.calculationMethod.percentage`}>
							<FormGroup label={intl.formatMessage({ id: "PERCENTAGE" })} overflow="hidden" required>
								<Controller
									name={`agreements.${index}.calculationMethod.percentage`}
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<NumberInput
											{...field}
											addonBefore={standardNegative ? "—" : undefined}
											addonAfter={"%"} />
									)} />
							</FormGroup>
						</FormField>
					</Grid>
				</>
			),
		},
		{
			id: "calculationMethod.applicableAgreementTypes",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.applicableAgreementTypes`}>
						<FormGroup label={intl.formatMessage({ id: "AGREEMENT_TYPES" })} overflow="hidden" required={isTankmatch}>
							<Controller
								name={`agreements.${index}.calculationMethod.applicableAgreementTypes`}
								control={control}
								rules={{ required: isTankmatch }}
								render={({ field }) => (
									<AgreementTypeSelect
										{...field}
										onChange={(value) => {
											field.onChange(value);
											const selectedTypes = Array.isArray(value) ? value : [];
											const matching = agreements
												.filter((a, i) => i !== index && selectedTypes.includes(a.type as AgreementTypeEnum))
												.map(a => ({ id: a.id, uuid: a.uuid, type: a.type, calculationMethod: a.calculationMethod }));
											setValue(`agreements.${index}.calculationMethod.applicableAgreements`, matching, { shouldDirty: true, shouldValidate: true });
										}}
										agreementTypes={agreementTypes}
										customAgreementTypes={customAgreementTypes}
										getOptionDisabled={option => (option === AgreementTypeEnum.AddressCommission || option === AgreementTypeEnum.Commission)}
										charterType={charterType}
										charterSubtype={charterSubtype}
										multiple={true}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.applicableAgreements",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.applicableAgreements`}>
						<FormGroup label={intl.formatMessage({ id: "APPLICABLE_AGREEMENTS" })} overflow="hidden" required={!isTankmatch}>
							<Controller
								name={`agreements.${index}.calculationMethod.applicableAgreements`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ApplicableAgreementsSelect
										{...field}
										multiple={true}
										agreements={agreements}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{PercentageOnAgreementsCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))}
		</>
	);
};

export default PercentageOnAgreementsCalculationMethodForm;
